import { Pressable } from "react-native";
import { View, Text } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { memo, useState, type FC } from "react";
import type { Clip } from "@src/features/clips/types/clip";
import { sizes } from "@src/constants/sizes";
import { Icon } from "@src/components/Icon";

interface ClipPreviewCardProps {
  clip: Clip;
  onPress: () => void;
  timezone: string;
}

export const ClipPreviewCard: FC<ClipPreviewCardProps> = memo(({ clip, onPress, timezone }) => {
  const { theme } = useUnistyles();
  const [imageError, setImageError] = useState(false);
  const timeDisplay = format(toZonedTime(new Date(clip.timestamp), timezone), "HH:mm:ss");

  return (
    <Pressable onPress={onPress}>
      <View
        width="100%"
        style={{
          aspectRatio: 16 / 10,
          borderRadius: theme.spacing.xl,
          overflow: "hidden",
          position: "relative",
        }}
        bg="muted"
      >
        {clip.thumbnailUrl && !imageError ?
          <img
            src={clip.thumbnailUrl}
            alt={`Clip ${clip.id}`}
            onError={() => setImageError(true)}
            loading="lazy"
            decoding="async"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        : <View flex={1} alignItems="center" justifyContent="center" bg="muted">
            <Icon name="videoOutline" size={theme.spacing["8xl"]} color={theme.colors.textMuted} />
          </View>
        }

        <View
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: [{ translateX: -theme.spacing["5xl"] }, { translateY: -theme.spacing["5xl"] }],
          }}
        >
          <View
            width={theme.spacing["10xl"]}
            height={theme.spacing["10xl"]}
            borderRadius={theme.spacing["5xl"]}
            alignItems="center"
            justifyContent="center"
            style={{
              backgroundColor: theme.colors.mediaOverlay.backgroundMedium,
            }}
          >
            <Icon
              name="play"
              size={theme.spacing["5xl"]}
              color={theme.colors.mediaOverlay.foreground}
              style={{ marginLeft: theme.spacing.xxs }}
            />
          </View>
        </View>

        <View
          style={{
            position: "absolute",
            bottom: theme.spacing.md,
            right: theme.spacing.md,
            backgroundColor: theme.colors.mediaOverlay.backgroundMedium,
            borderRadius: sizes.radiusCircle,
            paddingHorizontal: theme.spacing.md,
            paddingVertical: theme.spacing.xs,
            flexDirection: "row",
            alignItems: "center",
            gap: theme.spacing.xs,
          }}
        >
          <Icon name="clockOutline" size={theme.spacing["2xl"]} color={theme.colors.mediaOverlay.foreground} />
          <Text textStyle="xs/regular" style={{ color: theme.colors.mediaOverlay.foreground }}>
            {timeDisplay}
          </Text>
        </View>
      </View>
    </Pressable>
  );
});
